import pixAxios from '@/axios/pixAxios.js';
import Console from '@/shared/console';
import statusConst from '@/consts/status.const.js';
import GenericMarker from '@/assets/markers/researchComp.png';
import Vue from 'vue';
import pixGenericTable from '@/components/pix/pixGenericTable/pixGenericTable.vue';
import VueI18n from 'vue-i18n';
import i18nOptions from '@/services/i18n/i18nOptions.utils.js';
import genericTableService from '@/services/genericConfig/genericTable.utils.js';

const mandateDeprecated = {
  namespaced: true,

  state: {
    mandate: null,
    summary: null,
    usersAssigned: null, // array de string con nombre de usuarios asociados
    propertyGroups: [],
    canSave: null,
    updateUsers: false,
    loading: false,
    propertySearchGroupTable: null,
    propertySearchGroupTableStatus: statusConst.PENDING,
    totalRows: 0,
    sortFld: null,
    sortDir: null,
    totalPages: 0,
    nTake: 10,
    nSkip: 0,
    page: 1,
    pageSize: 5,
    pageSizeOptions: [
      { ValueDesc: '20', ValueCode: 20 },
      { ValueDesc: '50', ValueCode: 50 },
      { ValueDesc: '100', ValueCode: 100 },
      { ValueDesc: '200', ValueCode: 200 },
    ],
    markers: [],
    tableConfig: null,
    shouldCenterByMarkers: false,
  },

  getters: {
    getMandate(state) {
      return state.mandate;
    },
    getSummary(state) {
      return state.summary;
    },
    getUsersAssigned(state) {
      return state.usersAssigned;
    },
    getPropertyGroups(state) {
      return state.propertyGroups;
    },
    getPropertyGroupsTable(state) {
      return {
        Properties: state.propertySearchGroupTable,
        GroupName: state.propertyGroups[0]?.GroupName,
        JobRowId: state.propertyGroups[0]?.JobRowId,
        RowId: state.propertyGroups[0]?.RowId,
      };
    },
    getPropertySearchGroupTableStatus(state) {
      return state.propertySearchGroupTableStatus;
    },
    getCanSave(state) {
      return state.canSave;
    },
    getUpdateUsers(state) {
      return state.updateUsers;
    },
    getLoading(state) {
      return state.loading;
    },
    getTotalPages(state) {
      return state.totalPages;
    },
    getTotalRows(state) {
      return state.totalRows;
    },
    getSortFld(state) {
      return state.sortFld;
    },
    getSortDir(state) {
      return state.sortDir;
    },
    getNSkip(state) {
      return state.nSkip;
    },
    getPage(state) {
      return state.page;
    },
    getPageSize(state) {
      return state.pageSize;
    },
    getPageSizeOptions(state) {
      return state.pageSizeOptions;
    },
    getMarkers(state) {
      return state.markers;
    },
    getShouldCenterByMarkers(state) {
      return state.markers.length > 1;
    },
  },

  mutations: {
    setMandate(state, payload) {
      state.mandate = payload;
    },
    setSummary(state, payload) {
      state.summary = payload;
    },
    setUsersAssigned(state, payload) {
      state.usersAssigned = payload;
    },
    setPropertyGroups(state, payload) {
      state.propertyGroups = payload;
    },
    setPropertySearchGroupTable(state, payload) {
      state.propertySearchGroupTable = payload;
    },
    setPropertySearchGroupTableStatus(state, payload) {
      state.propertySearchGroupTableStatus = payload;
    },
    setPropertyGroupsPage(state, payload) {
      state.page = payload;
    },
    setPropertyGroupsTotalRows(state, payload) {
      state.totalRows = payload;
    },
    setPropertyGroupsPageSize(state, payload) {
      state.pageSize = payload;
    },
    setSortFld(state, payload) {
      state.sortFld = payload;
    },
    setSortDir(state, payload) {
      state.sortDir = payload;
    },
    setPropertyGroupsNSkip(state, payload) {
      state.nSkip = payload;
    },
    setPropertyGroupsTotalPage(state, payload) {
      state.totalPages = payload;
    },
    setCanSave(state, payload) {
      state.canSave = payload;
    },
    setUpdateUsers(state, payload) {
      state.updateUsers = payload;
    },
    setLoading(state, payload) {
      state.loading = payload;
    },

    setMarkers(state, payload) {
      state.markers = payload;
    },

    setTableConfig(state, payload) {
      state.tableConfig = payload;
    },
  },

  actions: {
    setSummary({ state, commit }, summary) {
      commit('setSummary', summary);
    },
    setUsersAssigned({ state, commit }, usersAssigned) {
      commit('setUsersAssigned', usersAssigned);
    },
    setCanSave({ state, commit }, canSave) {
      commit('setCanSave', canSave);
    },
    setUpdateUsers({ state, commit }, updateUsers) {
      commit('setUpdateUsers', updateUsers);
    },
    setPropertyGroupsPage({ state, commit }, page) {
      commit('setPropertyGroupsPage', page);
    },
    setPropertyGroupsNSkip({ state, commit }, skip) {
      commit('setPropertyGroupsNSkip', skip);
    },
    setSortFld({ state, commit }, fld) {
      commit('setSortFld', fld);
    },
    setSortDir({ state, commit }, dir) {
      commit('setSortDir', dir);
    },

    async loadGenericTableConfig({ state, commit }) {
      if (state.tableConfig) {
        return;
      }
      const response = await genericTableService.getGenericTableCall(107);
      commit('setTableConfig', response);
    },

    async fetchMandate({ commit }, jobRowId) {
      commit('setLoading', true);
      try {
        const response = await pixAxios.get(`api/Job/GetJobById?jobRowId=${jobRowId}&property=true`);
        commit('setMandate', response.data);
      } catch (error) {
        Console.log(error);
      } finally {
        commit('setLoading', false);
      }
    },

    async fetchPropertyGroups({ state, commit }, { jobRowId, isUpdateCallFromDelete }) {
      commit('setPropertySearchGroupTableStatus', statusConst.LOADING);
      let islastItemAndPage = false;

      /** Comprobamos si es la ultima pagina y el ultimo item solo al borrar elementos */
      if (isUpdateCallFromDelete) {
        islastItemAndPage = checkIsLastPageAndLastItem(state.totalRows, state.totalPages, state.pageSize, state.page);
      }

      if (islastItemAndPage && state.page > 1) {
        state.page = state.page - 1;
        state.nSkip = state.page * state.pageSize - state.pageSize;
      }

      try {
        const {
          data: { rows: groupRows },
        } = await pixAxios.get('api/JobPropertyGroup/SearchPropertyGroup', { params: { jobRowId, nSkip: state.nSkip, nTake: state.nTake, page: state.page, pageSize: state.pageSize, sortFld: state.sortFld, sortDir: state.sortDir } });

        const postData = { rowId: jobRowId, skip: state.nSkip, page: state.page, rows: state.pageSize, sortFld: state.sortFld, sortDir: state.sortDir };
        const {
          data: { rows, nSkip, nTake, page, pageSize, total },
        } = await pixAxios.post('api/Job/SearchPropertiesByIdJob', postData);

        commit('setPropertyGroups', groupRows);
        commit('setPropertySearchGroupTable', rows);
        commit('setPropertyGroupsTotalRows', total);
        commit('setPropertyGroupsPage', page);
        commit('setPropertyGroupsPageSize', pageSize);
        commit('setPropertyGroupsNSkip', nSkip);
        commit('setPropertyGroupsTotalPage', Math.ceil(total / pageSize));
      } catch (err) {
        Console.error(err);
      } finally {
        commit('setPropertySearchGroupTableStatus', statusConst.SUCCESS);
      }
    },

    async deleteProperty({ dispatch }, body) {
      try {
        const { data: succeed } = await pixAxios.post('/api/JobProperty/DeleteJobProperty', body);

        return succeed;
      } catch (err) {
        Console.log(err);
      }
    },

    async duplicateProperty({ dispatch }, body, jobRowId) {
      try {
        const { data: succeed } = await pixAxios.post('/api/JobProperty/DuplicateProperty', body);

        return succeed;
      } catch (err) {
        Console.log(err);
      }
    },

    async addProperty() {
      Console.info('To be implemented');
    },

    async setMarkers({ dispatch, commit, state }, mapId) {
      const propertyGroups = state.propertyGroups;
      const markers = [];
      if (propertyGroups) {
        propertyGroups.forEach((group) => {
          group?.Properties.forEach((property) => {
            markers.push({
              lat: property?.Latitude,
              lng: property?.Longitude,
              img: GenericMarker,
              infowindow: getMarkerInfowindow(property, state.tableConfig),
            });
          });
        });
      }

      if (markers.length) {
        commit('setMarkers', markers);
      }
      dispatch('pixMapModule/setMarkers', { mapId, markers: state.markers }, { root: true });
    },

    clearMandate({ commit }) {
      commit('setMandate', null);
      commit('setPropertyGroups', []);
      commit('setUsersAssigned', null);
      commit('setSummary', null);
    },

    clearPagination({ commit }) {
      commit('setPropertyGroupsNSkip', 0);
      commit('setPropertyGroupsPage', 1);
      commit('setPropertySearchGroupTable', null);
    },

    clearMarkers({ commit }) {
      commit('setMarkers', []);
    },
  },
};
export default mandateDeprecated;

const getMarkerInfowindow = function (property, tableConfig) {
  const ComponentClass = Vue.extend(pixGenericTable);

  const i18n = new VueI18n(i18nOptions);
  ComponentClass.use(i18n);
  const component = new ComponentClass({
    propsData: {
      columns: [...tableConfig],
      dataRows: [property],
      multiselect: false,
      readonly: true,
      uniqueTableName: `PixProperty_${property.RowId}`,
    },
    i18n,
  });
  const mountedComponent = component.$mount();
  return mountedComponent.$el;
};

const checkIsLastPageAndLastItem = function (totalItems, totalPages, pageSize, currentPage) {
  /** Estamos en la ultima pagina */
  if (currentPage === totalPages) {
    /** calculamos si el la ultima pagina en la que estamos existe un unico elemento*/
    const totalFullPage = Math.round(totalItems / pageSize);
    const totalElementsInFullPages = totalFullPage * pageSize;
    const leftoversElements = totalItems - totalElementsInFullPages;

    /** si existe un unico elemento devolvemos */
    if (leftoversElements === 1) {
      return true;
    } else {
      return false;
    }
  } else {
    return false;
  }
};
